/**
 * Запросы рядов «Голоса клиента» (`GET /analytics/voice/series`, #2394 §9
 * п. 7, спека docs/plans/2026-10-19-voc-trends-ui.md §2–§3).
 *
 * Проекты, категория, демо и фильтр темы — те же, что у запросов раздела
 * (`useVoiceRequestBase`): их считает один раз оболочка вкладок
 * (`VoiceSeriesScope`) и кладёт в контекст. Вне раздела (экран «Возражения»)
 * контекста нет — рядов нет, запросов тоже. Окно — вкладки
 * (`VoiceSeriesWindowContext`): то, по которому посчитаны её строки, — не
 * своё, иначе вкладка, открытая после полуночи, считала бы строки за новый
 * день, а ряд — за старый.
 *
 * Ряд — отдельный запрос: строки и пометки блока его не ждут, его сбой их не
 * трогает. Фильтр темы сужает ряд, как и строки, — кроме рядов тем: блок тем
 * фильтром не сужен (§2.5 компоновки), и `themeCode` у них не уходит.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import {
  notifyManager,
  skipToken,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryCacheNotifyEvent,
} from '@tanstack/react-query';
import type { VocFactSignal, VoiceTrendResponse } from '@aica/api-types/schemas';
import { sortedProjectIdsKey } from '@/lib/project-filter';
import { retryTransientOnly } from '@/lib/query-retry';
import { tierRefusalCode } from '@/lib/tier-refusal';
import { viewerTimeZone } from '@/lib/viewer-time-zone';
import { demoAsk, type VoiceRequestBase } from './useVoiceOfCustomer';
import type { VoiceRowDetailsTrend } from './voice-series-view-model';
import type { VoiceSearch } from './voice-search';
import {
  batchesFor,
  mergeSeries,
  rowSeries,
  seriesBatches,
  seriesCoverage,
  seriesHasPoints,
  seriesThemeGaps,
  seriesUnitFor,
  yearWindow,
  type VoiceRowSeries,
  type VoiceSeriesCoverage,
  type VoiceSeriesRef,
  type VoiceSeriesRequest,
  type VoiceSeriesRow,
  type VoiceSeriesUnit,
} from './voice-series-view-model';

/** Что ряд берёт у запросов раздела (`useVoiceRequestBase`) — без окна: его ставит вкладка. */
export type VoiceSeriesBase = Pick<
  VoiceRequestBase,
  | 'apiClient'
  | 'orgId'
  | 'includeDemo'
  | 'lockedDemo'
  | 'demoReady'
  | 'category'
  | 'voiceTheme'
> &
  Pick<VoiceRequestBase['stream'], 'projectIds' | 'callCategory'>;

/** Область запросов рядов — та же, что у раздела; `null` — вне раздела. */
export interface VoiceSeriesScopeValue {
  base: VoiceSeriesBase;
  /**
   * Сделать периодом страницы месяц корзины вида «12 месяцев» (`instant` —
   * момент внутри него); `patch` — что ещё меняется в адресе (раздел строки).
   */
  showMonth: VoiceShowMonth;
}

export const VoiceSeriesScopeContext = createContext<VoiceSeriesScopeValue | null>(null);

export type VoiceShowMonth = (instant: string, patch?: Partial<VoiceSearch>) => void;

/** «Показать этот месяц» из раскрытой строки; `null` — вне раздела. */
export function useVoiceShowMonth(): VoiceShowMonth | null {
  return useContext(VoiceSeriesScopeContext)?.showMonth ?? null;
}

/** Окно, по которому вкладка посчитала свои строки. */
export interface VoiceSeriesWindow {
  periodStart: string;
  periodEnd: string;
  /** The window's last day in its own zone — an organization's window (`windowLastDay`). */
  lastDay?: string;
}

/**
 * Окно рядов вкладки — ставят блоки вкладки (`VoiceThemeFilterBlocks`).
 * `null` — строки на экране ещё прошлого окна (ответ нового в пути): ряды
 * ждут его, а не рисуются рядом со старыми числами. Нет провайдера — рядов нет.
 */
export const VoiceSeriesWindowContext = createContext<
  VoiceSeriesWindow | null | undefined
>(undefined);

/** Окно рядов вкладки: окно её строк, пока ни одна из них не из прошлого окна. */
export function tabSeriesWindow(
  range: VoiceSeriesWindow,
  answers: readonly { isPlaceholderData: boolean }[]
): VoiceSeriesWindow | null {
  return answers.some((answer) => answer.isPlaceholderData) ? null : range;
}

interface SeriesWindow extends VoiceSeriesWindow {
  unit: VoiceSeriesUnit;
}

/** Фильтр темы у ряда: блок тем им не сужен (§2.5 компоновки). */
const seriesThemeCode = (base: VoiceSeriesBase, signal: VocFactSignal) =>
  signal === 'theme' ? undefined : base.voiceTheme;

/**
 * Ключ области рядов сигнала — всё, кроме ключей строк: за ним идёт один
 * элемент, ключи куска (`seriesQuery`). Карточка сводки находит по нему
 * куски блока.
 */
function seriesScopeKey(
  base: VoiceSeriesBase,
  window: SeriesWindow,
  timeZone: string,
  signal: VocFactSignal
) {
  return [
    'analytics',
    'voice-series',
    base.orgId,
    window.periodStart,
    window.periodEnd,
    window.unit,
    timeZone,
    sortedProjectIdsKey(base.projectIds),
    base.callCategory,
    base.includeDemo,
    // The demo of a closed section and the organization's own series are
    // different answers (#2816), as in every other voice key.
    base.lockedDemo ? 'demo' : 'own',
    seriesThemeCode(base, signal) ?? null,
    signal,
  ] as const;
}

/** Ключи куска строкой — последний элемент его ключа. */
const SERIES_KEYS_SEPARATOR = '\n';

/** Ключ и запрос одного ряда — без хуков. */
function seriesQuery(
  base: VoiceSeriesBase,
  window: SeriesWindow,
  timeZone: string,
  signal: VocFactSignal,
  keys: readonly string[]
) {
  const { apiClient, orgId, projectIds, includeDemo, category, lockedDemo } = base;
  const themeCode = seriesThemeCode(base, signal);
  return {
    queryKey: [
      ...seriesScopeKey(base, window, timeZone, signal),
      keys.join(SERIES_KEYS_SEPARATOR),
    ] as const,
    queryFn: () =>
      apiClient.reports.getVoiceSeries(
        {
          signal,
          keys,
          bucket: window.unit,
          timeZone,
          periodStart: window.periodStart,
          periodEnd: window.periodEnd,
          projectIds: projectIds.length > 0 ? [...projectIds] : undefined,
          callCategory: category,
          includeDemo,
          ...(themeCode ? { themeCode } : {}),
        },
        ...demoAsk({ lockedDemo })
      ),
    enabled: Boolean(orgId) && base.demoReady,
    retry: retryTransientOnly,
  };
}

/**
 * Почему ряд отказан насовсем — повтор вернёт тот же отказ: шаг сигнала
 * выключен (`signal_disabled`), тариф, иначе (фильтр темы, доступ).
 */
export type VoiceSeriesRefusal = 'disabled' | 'plan' | 'other';

/**
 * 4xx, который повтор не снимет: 408 и 429 (лимитер) проходят, когда окно
 * сбросится, — это сбой с «Повторить», а не отказ.
 */
const isRefusalStatus = (status: unknown): boolean =>
  typeof status === 'number' &&
  status >= 400 &&
  status < 500 &&
  status !== 408 &&
  status !== 429;

/** Отказ всех упавших кусков, если каждый — отказ 4xx; `null` — есть сбой, который повтор может снять. */
export function seriesRefusal(errors: readonly unknown[]): VoiceSeriesRefusal | null {
  const statuses = errors.map((error) => (error as { status?: unknown } | null)?.status);
  if (errors.length === 0 || !statuses.every(isRefusalStatus)) {
    return null;
  }
  if (errors.some((error) => tierRefusalCode(error) !== null)) return 'plan';
  const disabled = errors.every(
    (error) =>
      ((error as { data?: { code?: unknown } | null }).data?.code ?? null) ===
      'signal_disabled'
  );
  return disabled ? 'disabled' : 'other';
}

/** Что блок говорит о своих рядах — строка над строками (§1 спеки). */
export interface VoiceSeriesBlock {
  /**
   * `off` — у блока рядов нет (вне раздела, браузер не назвал пояс, строки
   * без ряда или без K); `pending`; `failed` (хотя бы один кусок, повтор
   * может помочь); `unavailable` — отказ насовсем (`refusal`); `ready`.
   */
  status: 'off' | 'pending' | 'failed' | 'unavailable' | 'ready';
  /** Почему отказан — только при `unavailable`. */
  refusal: VoiceSeriesRefusal | null;
  unit: VoiceSeriesUnit;
  /** Покрытие корзин сигналов, что нарисовали ряды, — когда все запросы ответили. */
  coverage: VoiceSeriesCoverage | null;
  /** Внутри окна менялся набор кодов — ряды с разрывом. */
  breaks: boolean;
  /**
   * Сигналы, часть ключей которых сведена по другой версии: конкуренты —
   * глоссария (или он не загрузился), возражения — карты меток.
   */
  stale: VocFactSignal[];
  /** У темы, появившейся внутри окна, корзины до её начала пусты. */
  themeGaps: boolean;
  retry: () => void;
  /** Ряд строки; `null` — нет (ещё не пришёл, сбой, строка без ряда). */
  rowSeries: (ref: VoiceSeriesRef | null, calls: number) => VoiceRowSeries | null;
}

const NO_SERIES = () => null;

/** Область, окно и пояс рядов; `null` — рядов нет (`undefined` окна) или ждут (`null`). */
function useSeriesFrame() {
  const base = useContext(VoiceSeriesScopeContext)?.base ?? null;
  const window = useContext(VoiceSeriesWindowContext);
  // A series in another zone would cut other days than the screen's window:
  // without the browser's zone there is none, not a UTC one.
  const timeZone = viewerTimeZone();
  const off = !base || window === undefined || !timeZone;
  return { base, window: off ? undefined : window, timeZone: timeZone ?? '' };
}

/**
 * Ряды строк блока по окну экрана — день или неделя. Один запрос на сигнал
 * (кусками по 20 ключей), только строки с разговорами в окне или в прошлом.
 */
export function useVoiceRowsSeries(rows: readonly VoiceSeriesRow[]): VoiceSeriesBlock {
  const { base, window, timeZone } = useSeriesFrame();
  const unit = window ? seriesUnitFor(window.periodStart, window.periodEnd) : 'day';
  // The batches asked in this window so far: a row shown later («Show all
  // N», the next page) is asked on its own, the asked ones keep their cache.
  // Kept as derived state — set during render when a row brings a new key or
  // the window changes, the documented pattern for state from earlier renders.
  const scope = window ? `${window.periodStart}|${window.periodEnd}` : '';
  const [asked, setAsked] = useState<{
    scope: string;
    batches: readonly VoiceSeriesRequest[];
  }>({ scope, batches: [] });
  const previous = asked.scope === scope ? asked.batches : [];
  const batches = seriesBatches(previous, rows);
  if (asked.scope !== scope || batches !== asked.batches) setAsked({ scope, batches });
  const requests = base && window !== undefined ? batchesFor(batches, rows) : [];
  const results = useQueries({
    queries:
      base && window
        ? requests.map((request) =>
            seriesQuery(base, { ...window, unit }, timeZone, request.signal, request.keys)
          )
        : [],
  });

  const retry = () => {
    for (const result of results) if (result.isError) void result.refetch();
  };
  const empty = {
    refusal: null,
    unit,
    coverage: null,
    breaks: false,
    stale: [],
    themeGaps: false,
    retry,
    rowSeries: NO_SERIES,
  };
  if (requests.length === 0) return { ...empty, status: 'off' };
  // The rows on screen are still the previous window's: the series waits.
  if (!window) return { ...empty, status: 'pending' };
  const failedSignals = new Set(
    requests
      .filter((_, index) => results[index]?.isError)
      .map((request) => request.signal)
  );
  const failed = failedSignals.size > 0;
  // Refused for good (a switched-off step, the plan): said once, without a
  // retry that can only fail again.
  const refusal = seriesRefusal(
    results.filter((result) => result.isError).map((result) => result.error)
  );
  const pending = results.some((result) => result.isPending);
  const bySignal = new Map<VocFactSignal, VoiceTrendResponse[]>();
  requests.forEach((request, index) => {
    const data = results[index]?.data;
    if (!data) return;
    bySignal.set(request.signal, [...(bySignal.get(request.signal) ?? []), data]);
  });
  const merged = new Map(
    [...bySignal].map(([signal, parts]) => [signal, mergeSeries(parts)] as const)
  );
  // What the note says about the lines on screen — the signals that drew:
  // a refused or failed signal of a mixed block («What changed») does not
  // blank the coverage, gaps and breaks of the others' lines.
  const responses = [...merged]
    .filter(([signal]) => !failedSignals.has(signal))
    .map(([, response]) => response)
    .filter((response): response is VoiceTrendResponse => response !== null);
  const ready = !pending;
  return {
    status: refusal ? 'unavailable' : failed ? 'failed' : pending ? 'pending' : 'ready',
    refusal,
    unit,
    coverage:
      ready && responses[0] ? seriesCoverage(responses.flatMap((r) => r.buckets)) : null,
    breaks: ready && responses.some((response) => response.breaks.length > 0),
    stale: ready
      ? responses.filter((response) => response.resolutionStale).map((r) => r.signal)
      : [],
    themeGaps:
      ready && !failedSignals.has('theme') && seriesThemeGaps(merged.get('theme')),
    retry,
    // A failed chunk draws none of its signal: half a block of sparklines
    // next to a «failed» line would look like data for the rest. The other
    // signals of the block («What changed») keep theirs.
    rowSeries: (ref, calls) =>
      ref && !failedSignals.has(ref.signal)
        ? rowSeries(merged.get(ref.signal), ref, calls)
        : null,
  };
}

/** Ряды блока и ряд каждой его строки — одна проводка на все блоки. */
export function useVoiceBlockSeries<T>(
  rows: readonly T[],
  seriesRow: (row: T) => VoiceSeriesRow
): {
  block: VoiceSeriesBlock;
  trendOf: (row: T) => VoiceRowDetailsTrend | undefined;
} {
  const block = useVoiceRowsSeries(rows.map(seriesRow));
  const trendOf = (row: T) => {
    const { ref: seriesRef, calls } = seriesRow(row);
    const series = block.rowSeries(seriesRef, calls);
    return seriesRef && series ? { seriesRef, series, unit: block.unit } : undefined;
  };
  return { block, trendOf };
}

/**
 * Слушатель кеша для читателей рядов: зовёт `onChange` только на события
 * запросов рядов и пачкой, как `useIsFetching` (#2712), — остальной кеш
 * (строки, сводки, поиск) карточку не будит.
 */
export function onVoiceSeriesEvent(
  onChange: () => void
): (event: QueryCacheNotifyEvent) => void {
  const notify = notifyManager.batchCalls(onChange);
  return (event) => {
    if (event.query.queryKey[1] === 'voice-series') notify();
  };
}

/** Куски ряда строкой в снимке кеша — список ключей куска; строка, чтобы снимок был стабилен. */
const CHUNKS_SEPARATOR = '\u0000';

/**
 * Ряд строки из ответов, которые уже пришли блоку, — у карточки сводки
 * (#2553). Своего запроса нет: ряд просит только открытый раздел (§2 спеки),
 * и карточка рисует спарклайн из ответа блока вкладки (её раздела или «Что
 * изменилось»), где строка была на экране в этом окне и области. Карточка
 * наблюдает эти ответы, не запрашивая их: закрытый раздел не уносит их из
 * кеша, пока вкладка на экране.
 *
 * Ряд устаревает вместе с ответом счёта (#2712): пришёл ответ с другим
 * содержимым (переподключение, инвалидация) — ряды этой области помечаются
 * устаревшими; открытый блок переспрашивает свой кусок, закрытый — когда его
 * откроют, а до нового ответа карточка линии не рисует. Ответ с тем же
 * содержимым (тот же объект кеша) рядов не трогает. Упавший кусок своих чисел
 * не дал и ничего не гасит; разрывы — по всем пришедшим кускам сигнала, как у
 * блока (`mergeSeries`). Строка, которую спросили два блока, берётся вместе с
 * корзинами (знаменателем долей) из одного ответа: того, что рисует открытый
 * блок, иначе более позднего.
 */
export function useVoiceLoadedRowSeries(
  ref: VoiceSeriesRef | null,
  calls: number,
  answer: unknown
): { series: VoiceRowSeries; unit: VoiceSeriesUnit } | null {
  const { base, window, timeZone } = useSeriesFrame();
  const queryClient = useQueryClient();
  const cache = queryClient.getQueryCache();
  const unit = window ? seriesUnitFor(window.periodStart, window.periodEnd) : 'day';
  const frame = base && window && ref ? { base, window: { ...window, unit }, ref } : null;
  const scope = frame
    ? seriesScopeKey(frame.base, frame.window, timeZone, frame.ref.signal)
    : null;
  const scopeId = scope ? JSON.stringify(scope) : null;
  // The count answered anew in the same scope: its series are out of date. A
  // new scope (filter, window) is a new count, not a newer one.
  const seen = useRef({ scopeId, answer });
  useEffect(() => {
    if (answer === undefined) return;
    const previous = seen.current;
    seen.current = { scopeId, answer };
    if (
      scope &&
      previous.scopeId === scopeId &&
      previous.answer !== undefined &&
      previous.answer !== answer
    ) {
      void queryClient.invalidateQueries({ queryKey: scope });
    }
  }, [queryClient, scope, scopeId, answer]);
  const subscribe = useCallback(
    (onChange: () => void) => cache.subscribe(onVoiceSeriesEvent(onChange)),
    [cache]
  );
  // The keys of every answered chunk of the scope not out of date — the open
  // block's first, then the newest — if one of them holds the row.
  const chunks = useSyncExternalStore(subscribe, (): string => {
    if (!scope || !frame) return '';
    const fresh = cache
      .findAll({ queryKey: scope })
      .filter(
        (candidate) =>
          candidate.state.status === 'success' && !candidate.state.isInvalidated
      )
      .sort(
        (a, b) =>
          Number(b.isActive()) - Number(a.isActive()) ||
          b.state.dataUpdatedAt - a.state.dataUpdatedAt
      )
      .map((candidate) => String(candidate.queryKey[scope.length]));
    const holds = fresh.some((keys) =>
      keys.split(SERIES_KEYS_SEPARATOR).includes(frame.ref.key)
    );
    return holds ? fresh.join(CHUNKS_SEPARATOR) : '';
  });
  const results = useQueries({
    queries:
      frame && chunks
        ? chunks.split(CHUNKS_SEPARATOR).map((keys) => ({
            ...seriesQuery(
              frame.base,
              frame.window,
              timeZone,
              frame.ref.signal,
              keys.split(SERIES_KEYS_SEPARATOR)
            ),
            // Observed, never asked: the block asks it.
            enabled: false,
          }))
        : [],
  });
  const answers = results.flatMap((result) => (result.data ? [result.data] : []));
  const merged = mergeSeries(answers);
  // The row and the buckets it is a share of come from one answer; the breaks
  // are the signal's, from every chunk, as the block's.
  const own = frame
    ? answers.find((part) => part.rows.some((row) => row.key === frame.ref.key))
    : undefined;
  const series =
    own && merged
      ? rowSeries(
          { ...own, breaks: merged.breaks, resolutionStale: merged.resolutionStale },
          frame?.ref ?? null,
          calls
        )
      : null;
  return series && seriesHasPoints(series) ? { series, unit } : null;
}

/** Ряд строки за 12 месяцев по месяцам — вид раскрытой строки, по запросу. */
export function useVoiceYearSeries(ref: VoiceSeriesRef | null, enabled: boolean) {
  const { base, window, timeZone } = useSeriesFrame();
  const query =
    base && window && ref
      ? seriesQuery(
          base,
          {
            ...yearWindow(window.periodStart, window.periodEnd, window.lastDay),
            unit: 'month',
          },
          timeZone,
          ref.signal,
          [ref.key]
        )
      : null;
  return useQuery<VoiceTrendResponse>({
    queryKey: query?.queryKey ?? ['analytics', 'voice-series', 'none'],
    // Outside the section nothing is asked: no query function to call.
    queryFn: query?.queryFn ?? skipToken,
    enabled: query !== null && query.enabled && enabled,
    retry: retryTransientOnly,
  });
}
